import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { getEncryptedItem, setEncryptedItem } from '../utils/encryptedStorage';

export type ColorScheme = 'light' | 'dark' | 'auto';

interface PreferencesState {
  colorScheme: ColorScheme;
  accentColor: string;
  language: string;
  timezone: string;
  locale: string;
  reducedMotion?: boolean;
  highContrast?: boolean;
  fontScale?: 'small' | 'default' | 'large';
  density?: 'comfortable' | 'compact';
  strongFocus?: boolean;
}

interface PreferencesContextValue extends PreferencesState {
  setPreference: <K extends keyof PreferencesState>(key: K, value: PreferencesState[K]) => void;
  resetPreferences: () => void;
}

const DEFAULT_PREFERENCES: PreferencesState = {
  colorScheme: 'auto',
  accentColor: 'indigo',
  language: 'en',
  timezone: 'UTC',
  locale: 'en',
  reducedMotion: false,
  density: 'comfortable',
  highContrast: false,
  fontScale: 'default',
  strongFocus: true,
};

const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<PreferencesState>(DEFAULT_PREFERENCES);

  useEffect(() => {
    const stored = getEncryptedItem<PreferencesState>('preferences');
    if (stored) {
      setPreferences({ ...DEFAULT_PREFERENCES, ...stored });
    }
  }, []);

  const setPreference = useCallback(
    <K extends keyof PreferencesState>(key: K, value: PreferencesState[K]) => {
      setPreferences((prev) => {
        const next = { ...prev, [key]: value };
        setEncryptedItem('preferences', next);
        return next;
      });
    },
    [],
  );

  const resetPreferences = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES);
    setEncryptedItem('preferences', DEFAULT_PREFERENCES);
  }, []);

  const value = useMemo(
    () => ({ ...preferences, setPreference, resetPreferences }),
    [preferences, setPreference, resetPreferences],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences() {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
  return ctx;
}
